/* TODO: Удалить строки с игнорированием TS в задаче PUB-4409 (Исправить ошибки типизации) */
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { computed, reactive, ref, toRaw } from 'vue'
import { defineStore } from 'pinia'
import { deepEqual } from 'fast-equals'
import { filterPrivate } from '@/utils/filters'
import { pipe } from '@/utils/pipe'
import { toISOStringWithTimezone } from '@/utils/dates'
import { ENewsTypeReverse } from '@/lib/references'
import { useNewsStore } from '@/ContextTab/stores/news'
import { usePredicates } from '@/ContextTab/stores/newsSet/usePredicates'
import { useGroupsAndUsersStore } from '@/ContextTab/stores/groupsAndUsers'
import { usePublishedFiltersStore } from '@/ContextTab/stores/newsSet/published/filters'
import { usePublishedLocalFiltersStore } from '@/ContextTab/stores/newsSet/published/localFilters'
import { apiClientV1, type ApiError } from '@/ContextTab/services/apiClient'
import type { GatewaySubscriptionHubMessage } from '@/Workers/Gateway/types'
import type { NewsSubscriptionData } from '@/Workers/Gateway/gwService/types'
import type { GetNewsSearchData } from '@/api/generated/v1'


const MAX_LIST_SIZE = 100
const QUERY_LIMIT = 100
export const MAX_SIZE_HEAD_SET = 1000

export const usePublishedNewsStore = defineStore('publishedNews', () => {

  const error = ref<ApiError[] | null>(null)

  const newsStore = useNewsStore()
  const groupsAndUsersStore = useGroupsAndUsersStore()
  const localFiltersStore = usePublishedLocalFiltersStore()
  const filterStore = usePublishedFiltersStore()

  const { publishedPredicates, checkAgainstPredicates, checkByNewsEvent } = usePredicates()

  const set = reactive<number[]>([])

  newsStore.registerNewsSetForGc(set)

  type LoadParams = {
    cursor?: string
    reverse?: boolean
    [key: string]: any
  }
  const params = reactive<LoadParams>({
    cursor: undefined,
    reverse: false,
  })

  const isFetching = ref(false)
  const isReversed = ref(false)
  const setReversed = (value: boolean, needReload = true) => {
    isReversed.value = value
    if (needReload) {
      reload()
    }
  }

  const datesFilter = computed(() => {
    const { start, end } = localFiltersStore.dates

    return {
      from_dt: start ? toISOStringWithTimezone(start) : null,
      to_dt: end ? toISOStringWithTimezone(end) : null,
    }
  })

  const getFilterParams = () => {
    const filters = pipe(toRaw, filterPrivate)(filterStore.filters)
    const processed = Object.keys(filters).reduce(
      (acc: Record<string, any>, cur: string) => {
        acc[cur] = filters[cur]?.join(',') ?? []
        return acc
      },
      {},
    )
    if (
      filters.user_group_ids?.length
      || filters.user_group_excluded_ids?.length
    ) {
      const userIncludedIds = filters?.users_ids ?? []
      const userExcludedIds = filters?.users_excluded_ids ?? []
      const allUserIds = [...userIncludedIds, ...userExcludedIds]

      const groupIds = (filters.user_group_ids ?? []).reduce(
        (acc: (string | number)[], val: string | number) => {
          const groupChildren = groupsAndUsersStore.groupAndUsersById[`${val}G`]?.children ?? []
          return [
            ...acc,
            ...groupChildren.filter(
              (child: string | number) => !allUserIds.includes(child),
            ),
          ]
        },
        userIncludedIds,
      )

      const groupExcludedIds = (filters.user_group_excluded_ids ?? []).reduce(
        (acc: (string | number)[], val: string | number) => {
          const groupChildren = groupsAndUsersStore.groupAndUsersById[`${val}G`]?.children ?? []
          return [
            ...acc,
            ...groupChildren.filter(
              (child: string | number) => !allUserIds.includes(child),
            ),
          ]
        },
        userExcludedIds,
      )

      processed.user_ids = groupIds.join(',')
      processed.user_excluded_ids = groupExcludedIds.join(',')
    }
    return processed
  }

  const hasMoreItems = computed(() => params.cursor !== null)

  async function loadNext() {
    if (isFetching.value) {
      return
    }

    isFetching.value = true

    try {
      const { from_dt, to_dt } = datesFilter.value

      error.value = null

      const requestParams = {
        ...params,
        limit: QUERY_LIMIT,
        from_dt,
        to_dt,
        hide_republications: localFiltersStore.hideRepublications,
        reverse: isReversed.value,
        ...getFilterParams(),
      } satisfies GetNewsSearchData['query']

      const response = searchMode.value
        ? await apiClientV1.NewsService.getNewsSearch(
          { query: { ...requestParams, ...localFiltersStore.getSearchParams() } },
        )
        : await apiClientV1.NewsService.getNewsPublished({ query: requestParams })

      if (response.error) {
        error.value = response.error.errors

        if (response.error.errors.some((x) => x.code === 'UNPROCESSABLE_ENTITY')) {
          error.value.description = response.error.errors.find((x) => x.code === 'UNPROCESSABLE_ENTITY')?.title
        }
      } else {
        params.cursor = response.data.next_cursor
        const mapped = response.data.data
          ?.map((item) => item.id)
          .filter((id: number) => !set.includes(id)) ?? []

        if (isReversed.value) {
          set.unshift(...mapped)
        } else {
          set.push(...mapped)
        }

        response.data.data?.forEach((newsItem) => {
          newsStore.setNewsItem(newsItem.id, newsItem)
        })
      }
    } finally {
      isFetching.value = false
    }
  }

  async function reload() {
    params.cursor = undefined
    set.length = 0
    isFullHeadSet.value = false
    lockedHeadSet.length = 0
    isLockedHead.value = searchMode.value
    await loadNext()
  }

  function slice(size = MAX_LIST_SIZE) {
    if (set.length > MAX_LIST_SIZE && isFetching.value === false) {
      const item = newsStore.news[set[MAX_LIST_SIZE]]
      params.cursor = item?.status_modified_at
      set.length = size
    }
  }

  function unshiftSet(id: number, makeSlice = false) {
    if (set.includes(id)) {
      return
    }
    set.unshift(id)
    if (makeSlice) {
      slice()
    }
  }

  const newsList = computed(() => set.map((id) => newsStore.news[id]))


  // Переключение на режим поиска и обратно --------------------

  const searchMode = ref(false)
  const showSearch = ref(false)

  const updateShowSearch = (show: boolean) => {
    showSearch.value = show
  }

  const cachedSet = reactive<number[]>([])

  newsStore.registerNewsSetForGc(cachedSet)

  let cachedCursor: string | undefined = undefined
  let prevParams = {}

  async function switchMode() {
    searchMode.value = !searchMode.value
    isReversed.value = false

    if (searchMode.value) {
      prevParams = getBothModesCommonFilterParams()
      toggleLockHead(true)
      cache()
      await search()
    } else {
      const paramsChanged = !deepEqual(
        prevParams,
        getBothModesCommonFilterParams(),
      )
      if (paramsChanged) {
        await reload()
      } else {
        getCached()
        toggleLockHead(false)
      }
    }
  }

  function cache() {
    cachedSet.push(...set)
    cachedCursor = params.cursor
  }

  function getCached() {
    set.length = 0
    set.push(...cachedSet)
    cachedSet.length = 0
    params.cursor = cachedCursor
    cachedCursor = undefined
  }

  async function search() {
    params.cursor = undefined
    set.length = 0
    await loadNext()
  }

  function getBothModesCommonFilterParams() {
    const { from_dt, to_dt } = datesFilter.value

    return {
      from_dt,
      to_dt,
      hide_republications: localFiltersStore.hideRepublications,
      ...getFilterParams(),
    }
  }


  // Складывание новостей в плашку "Есть новые новости N" -----------

  const isLockedHead = ref<boolean>(false)
  const lockedHeadSet = reactive<number[]>([])

  newsStore.registerNewsSetForGc(lockedHeadSet)

  const isFullHeadSet = ref(false)

  function pushLockedHeadSet(id: number) {
    if (isFullHeadSet.value) {
      return
    }

    lockedHeadSet.push(id)

    if (lockedHeadSet.length >= MAX_SIZE_HEAD_SET) {
      isFullHeadSet.value = true
    }
  }

  function toggleLockHead(isLocked: boolean) {
    if (!isLocked) {
      if (searchMode.value) {
        return
      }

      if (isFullHeadSet.value) {
        reload()
      } else {
        lockedHeadSet.forEach((id) => unshiftSet(id))
      }

      lockedHeadSet.length = 0
    }
    isLockedHead.value = isLocked
  }

  // don't rely on the value! only use to watch news count changed! (used for sound notifications)
  const urgentNewsSignal = ref(0)

  async function onGwNewsMessage(message: GatewaySubscriptionHubMessage) {
    if (!message.payload.data) {
      return
    }
    const data = message.payload.data as NewsSubscriptionData

    const checked = await checkAgainstPredicates(publishedPredicates, data.news)
      && checkByNewsEvent(['LINKS_DELETED'], data.type_id)

    /* Если новость соответствует всем predicates и ее еще нет в массиве set,
     * то добавляем ее id в начало, сортировку по времени обеспечивает сокет (?),
     * т.е. новая новость всегда будет более свежая, чем ранее полученные */
    if (checked && !set.includes(data.news.id)) {
      /* Если скролл заблокирован, то складываем новости в отдельную очередь,
       * в противном случае складываем в основную очередь */
      if (isLockedHead.value) {
        pushLockedHeadSet(data.news.id)
      } else {
        unshiftSet(data.news.id, true)
      }

      if (
        data.news.version?.type_id === ENewsTypeReverse.FLASH
        || data.news.version?.type_id === ENewsTypeReverse.EXPRESS
      ) {
        urgentNewsSignal.value++
      }
    }
  }

  function clearStore() {
    set.length = 0
    params.cursor = undefined
  }

  return {
    error,
    set,
    newsList,
    hasMoreItems,
    urgentNewsSignal,
    isFetching,
    isReversed,
    setReversed,
    isLockedHead,
    lockedHeadSet,
    pushLockedHeadSet,
    toggleLockHead,

    loadNext,
    reload,
    slice,
    unshiftSet,

    searchMode,
    showSearch,
    cachedSet,
    switchMode,
    updateShowSearch,
    search,
    clearStore,

    onGwNewsMessage,
  }
})
